import { AnswerLanguage, ConversationHistory } from '@glow/ai-contract'
import { IconFa, PlainButton } from '@glow/ui-components'
import i18next from 'i18next'
import { FormEvent, ReactNode, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useOtherAppLabels } from '../../i18n/otherAppLabels'
import { canUseAskGlow } from '../../utils/access'
import { AppStateType } from '../../utils/appStateReduxStore'
import { OtherAppLabelsLoadedContext } from './AiAnswer'
import { AiChatLauncher } from './AiChatLauncher'
import { AiChatTurn } from './AiChatTurn'
import { AiHistory } from './AiHistory'
import { AiLanguagePicker } from './AiLanguagePicker'
import { AiLogo } from './AiLogo'
import { AiThinkingParcel } from './AiThinkingParcel'
import { AiThinkingRoute } from './AiThinkingRoute'
import { readAnswerLanguage, storeAnswerLanguage } from './aiAnswerLanguage'
import { deleteAiConversation, fetchAiConversation, fetchAiConversations } from './aiConversations'
import { AiChatOptions, useAiChat } from './useAiChat'

const SUGGESTION_KEYS = ['dispatch', 'statuses', 'recurring', 'deviation'] as const

// The header fits its title, the answer-language picker on its longest name and three buttons from here up
const MIN_DRAWER_WIDTH = 460
const MAX_DRAWER_WIDTH = 800
const DEFAULT_DRAWER_WIDTH = 480
// Keyboard resize step for the drag handle, in px per arrow press
const RESIZE_STEP = 40
// The question field grows with its text up to about five lines, then scrolls
const MAX_INPUT_HEIGHT = 120
const drawerWidthStorageKey = 'aiChat.width'
// Where the removed model picker kept its choice; cleared so no stale pick outlives it
const retiredModelStorageKey = 'aiChat.model'

const clampWidth = (width: number) => Math.min(MAX_DRAWER_WIDTH, Math.max(MIN_DRAWER_WIDTH, width))

const readStoredWidth = (): number => {
  const stored = Number(globalThis.localStorage.getItem(drawerWidthStorageKey))
  return Number.isFinite(stored) && stored > 0 ? clampWidth(stored) : DEFAULT_DRAWER_WIDTH
}

export const AiChat = (options: AiChatOptions) => {
  const userId = useSelector((state: AppStateType) => state.get('user').get('id'))
  const available = useSelector(canUseAskGlow)

  // Keyed on the user, so logout or a user switch discards the conversation and aborts any stream
  return userId && available ? <Chat key={userId} userId={userId} {...options} /> : null
}

const HeaderButton = ({
  onClick,
  label,
  disabled,
  children
}: {
  onClick: () => void
  label: string
  disabled?: boolean
  children: ReactNode
}) => (
  <PlainButton onClick={onClick} aria-label={label} disabled={disabled} className="px-1 text-emphasis-medium">
    {children}
  </PlainButton>
)

const Chat = ({ userId, ...options }: AiChatOptions & { userId: number }) => {
  const {
    fetchConversations = fetchAiConversations,
    fetchConversation = fetchAiConversation,
    deleteConversation = deleteAiConversation
  } = options
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<'chat' | 'history'>('chat')
  const [width, setWidth] = useState(readStoredWidth)
  const [input, setInput] = useState('')
  const [answerLanguage, setAnswerLanguage] = useState(() => readAnswerLanguage(userId))
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const launcherRef = useRef<HTMLButtonElement>(null)
  const wasOpenRef = useRef(false)
  const { messages, status, send, retry, rate, sendComment, stop, reset, forgetConversation, loadConversation } =
    useAiChat(options, answerLanguage)
  const labelsLoaded = useOtherAppLabels(open)

  useEffect(() => {
    if (open) {
      wasOpenRef.current = true
      inputRef.current?.focus()
    } else if (wasOpenRef.current) {
      launcherRef.current?.focus()
    }
  }, [open])

  useEffect(() => {
    globalThis.localStorage.removeItem(retiredModelStorageKey)
  }, [])

  useEffect(() => {
    if (!open) {
      return
    }
    // Escape inside a confirmation or an open dropdown in the drawer closes only that one
    const onKeyDown = (event: KeyboardEvent) =>
      event.key === 'Escape' &&
      !(event.target instanceof Element && event.target.closest('dialog, [aria-expanded="true"]')) &&
      setOpen(false)
    globalThis.addEventListener('keydown', onKeyDown)
    return () => globalThis.removeEventListener('keydown', onKeyDown)
  }, [open])

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measured per keystroke, on resize and whenever the field mounts again with a draft
  useEffect(() => {
    const field = inputRef.current
    if (field) {
      field.style.height = 'auto'
      field.style.height = `${Math.min(field.scrollHeight, MAX_INPUT_HEIGHT)}px`
    }
  }, [input, open, view, width])

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-running on every messages change is the point - it follows the stream
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
  }, [messages, view])

  const streaming = status === 'streaming'

  // The indicators alternate per question, and stay fixed for the whole wait so the two
  // thinking slots (right after sending, then inside the empty answer) never swap mid-stream
  const questionCount = messages.filter((message) => message.role === 'user').length
  const thinking = questionCount % 2 === 1 ? <AiThinkingParcel /> : <AiThinkingRoute />

  const startNewChat = () => {
    reset()
    setView('chat')
    inputRef.current?.focus()
  }

  const showConversation = (history: ConversationHistory) => {
    loadConversation(history)
    setView('chat')
    inputRef.current?.focus()
  }

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    const question = input.trim()
    if (question && !streaming) {
      setInput('')
      send(question)
    }
  }

  return (
    <OtherAppLabelsLoadedContext value={labelsLoaded}>
      {!open && <AiChatLauncher ref={launcherRef} onOpen={() => setOpen(true)} />}

      {/* Above page content such as map controls (z-1000), below dialog backdrops (z-2000 and up) */}
      <aside
        aria-label={i18next.t('aiChat.title')}
        aria-hidden={!open}
        style={{ width }}
        className={`fixed right-0 top-0 z-1500 flex h-full max-w-[100vw] flex-col bg-white shadow-dark transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {open && (
          <>
            {/* The left edge resizes the drawer: drag, arrow keys, or double-click to reset */}
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label={i18next.t('aiChat.resize')}
              aria-valuemin={MIN_DRAWER_WIDTH}
              aria-valuemax={MAX_DRAWER_WIDTH}
              aria-valuenow={width}
              tabIndex={0}
              onPointerDown={(event) => {
                event.preventDefault()
                event.currentTarget.setPointerCapture(event.pointerId)
              }}
              onPointerMove={(event) => {
                if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                  setWidth(clampWidth(globalThis.innerWidth - event.clientX))
                }
              }}
              onPointerUp={() => globalThis.localStorage.setItem(drawerWidthStorageKey, String(width))}
              onDoubleClick={() => {
                setWidth(DEFAULT_DRAWER_WIDTH)
                globalThis.localStorage.setItem(drawerWidthStorageKey, String(DEFAULT_DRAWER_WIDTH))
              }}
              onKeyDown={(event) => {
                const delta = event.key === 'ArrowLeft' ? RESIZE_STEP : event.key === 'ArrowRight' ? -RESIZE_STEP : 0
                if (delta !== 0) {
                  event.preventDefault()
                  const next = clampWidth(width + delta)
                  setWidth(next)
                  globalThis.localStorage.setItem(drawerWidthStorageKey, String(next))
                }
              }}
              className="absolute left-0 top-0 z-10 h-full w-1.5 cursor-ew-resize touch-none hover:bg-primary-200 focus-visible:bg-primary-200 focus-visible:outline-none"
            />
            <header className="flex flex-wrap items-center gap-2 border-b border-gray-300 px-4 py-3">
              <AiLogo size={22} className="shrink-0 text-primary-500" />
              <span className="whitespace-nowrap font-semibold text-emphasis-high">{i18next.t('aiChat.title')}</span>
              <span className="rounded-full bg-primary-050 px-2 text-xs font-semibold text-primary-900">
                {i18next.t('aiChat.beta')}
              </span>
              <div className="ml-auto flex items-center gap-1">
                <AiLanguagePicker
                  language={answerLanguage}
                  onSelect={(language: AnswerLanguage) => {
                    setAnswerLanguage(language)
                    storeAnswerLanguage(userId, language)
                  }}
                />
                <HeaderButton onClick={startNewChat} label={i18next.t('aiChat.newChat')} disabled={streaming}>
                  <IconFa icon={['far', 'plus']} />
                </HeaderButton>
                {/* Disabled while streaming: the history view hides the Stop button and a
                    loaded conversation would fight the active stream over the messages */}
                <HeaderButton
                  onClick={() => setView(view === 'history' ? 'chat' : 'history')}
                  label={i18next.t('aiChat.history.open')}
                  disabled={streaming}
                >
                  <IconFa icon={['far', 'clock-rotate-left']} />
                </HeaderButton>
                <HeaderButton onClick={() => setOpen(false)} label={i18next.t('aiChat.close')}>
                  <IconFa icon={['far', 'times']} />
                </HeaderButton>
              </div>
            </header>

            {view === 'history' ? (
              <AiHistory
                fetchConversations={fetchConversations}
                fetchConversation={fetchConversation}
                deleteConversation={deleteConversation}
                onLoaded={showConversation}
                onDeleted={forgetConversation}
                onBack={() => setView('chat')}
              />
            ) : (
              <>
                {/* No aria-busy: it defers aria-live announcements until done and the global sanitize
                    rule turns the cursor into a progress spinner while the answer streams */}
                <div
                  ref={scrollRef}
                  aria-live="polite"
                  className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-4"
                >
                  {messages.length === 0 ? (
                    <div className="m-auto flex w-full max-w-xs flex-col items-center gap-4">
                      <p className="text-center text-sm text-emphasis-medium">{i18next.t('aiChat.empty')}</p>
                      <div className="flex w-full flex-col gap-2">
                        {SUGGESTION_KEYS.map((key) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => send(i18next.t(`aiChat.suggestions.${key}`))}
                            className="cursor-pointer rounded-full border border-gray-300 px-3 py-1.5 text-left text-sm text-emphasis-high hover:border-primary-500 hover:text-primary-500"
                          >
                            {i18next.t(`aiChat.suggestions.${key}`)}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    messages.map((message, index) => (
                      <AiChatTurn
                        key={message.id}
                        message={message}
                        streaming={streaming && index === messages.length - 1}
                        thinking={thinking}
                        onRate={rate}
                        onComment={sendComment}
                      />
                    ))
                  )}
                  {streaming && messages.at(-1)?.role === 'user' && <div className="self-start">{thinking}</div>}
                  {status === 'error' && (
                    <div className="flex items-center gap-3 rounded bg-primary-050 px-3 py-2">
                      <span className="text-sm text-emphasis-high">{i18next.t('aiChat.error')}</span>
                      <PlainButton onClick={retry} className="ml-auto text-sm font-semibold text-primary-500">
                        {i18next.t('aiChat.retry')}
                      </PlainButton>
                    </div>
                  )}
                </div>

                <p className="px-4 text-xs text-emphasis-medium">{i18next.t('aiChat.disclaimer')}</p>
                {/* The field grows upward from the drawer's bottom edge as the question wraps */}
                <form onSubmit={onSubmit} className="p-4 pt-2">
                  <div className="flex items-end gap-2 rounded-lg border border-gray-300 px-3 py-2 focus-within:border-primary-500">
                    <textarea
                      ref={inputRef}
                      rows={1}
                      value={input}
                      onChange={(event) => setInput(event.target.value)}
                      onKeyDown={(event) => {
                        // Safari reports an IME's confirming Enter as keyCode 229 without isComposing
                        const composing = event.nativeEvent.isComposing || event.keyCode === 229
                        if (event.key === 'Enter' && !event.shiftKey && !composing) {
                          event.preventDefault()
                          event.currentTarget.form?.requestSubmit()
                        }
                      }}
                      placeholder={i18next.t('aiChat.placeholder')}
                      aria-label={i18next.t('aiChat.placeholder')}
                      className="min-h-7 w-full resize-none overflow-y-auto bg-transparent py-0.5 text-emphasis-high outline-none"
                    />
                    <div className="flex shrink-0 items-center">
                      {streaming ? (
                        <button
                          type="button"
                          onClick={stop}
                          aria-label={i18next.t('aiChat.stop')}
                          className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-gray-100 text-emphasis-high hover:bg-gray-300"
                        >
                          <IconFa icon={['far', 'times']} size="sm" />
                        </button>
                      ) : (
                        <button
                          type="submit"
                          aria-label={i18next.t('aiChat.send')}
                          disabled={!input.trim()}
                          className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-white transition-colors hover:bg-primary-900 disabled:cursor-not-allowed disabled:bg-gray-300 enabled:cursor-pointer"
                        >
                          <IconFa icon={['far', 'paper-plane-top']} size="sm" />
                        </button>
                      )}
                    </div>
                  </div>
                </form>
              </>
            )}
          </>
        )}
      </aside>
    </OtherAppLabelsLoadedContext>
  )
}
